import React, { useState, useEffect, lazy, Suspense } from 'react';
import { MapPin, Truck, Star, CreditCard, Route, Calendar, Weight, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { earningsApi } from '../../lib/api';
import { formatPrice } from '../../lib/utils';
import { toast } from 'sonner';

const RouteMap = lazy(() => import('../../components/RouteMap'));

const statusLabels = {
  delivered: 'Livré',
  in_progress: 'En cours',
  accepted: 'Accepté',
};

const statusColors = {
  delivered: 'bg-green-100 text-green-700',
  in_progress: 'bg-blue-100 text-blue-700',
  accepted: 'bg-orange-100 text-orange-700',
};

const CarrierHistoryPage = () => {
  const [earnings, setEarnings] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDelivery, setSelectedDelivery] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [earningsRes, historyRes] = await Promise.all([
          earningsApi.getEarnings(),
          earningsApi.getHistory(),
        ]);
        setEarnings(earningsRes.data);
        setHistory(historyRes.data);
        if (historyRes.data.deliveries?.length > 0) {
          setSelectedDelivery(historyRes.data.deliveries[0]);
        }
      } catch (err) {
        toast.error('Erreur lors du chargement');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="carrier-history-page">
      <div>
        <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-900 uppercase">
          Historique & Statistiques
        </h1>
        <p className="text-slate-500 mt-1">Vos trajets, livraisons et revenus</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="carrier-stats">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900" data-testid="total-earnings">
                  {formatPrice(earnings?.total_earnings || 0)}
                </p>
                <p className="text-xs text-slate-500">Revenus totaux</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Truck className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900" data-testid="delivered-count">
                  {earnings?.delivered_count || 0}
                </p>
                <p className="text-xs text-slate-500">Livraisons</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Star className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900" data-testid="average-rating">
                  {earnings?.average_rating ? `${earnings.average_rating.toFixed(1)}/5` : '-'}
                </p>
                <p className="text-xs text-slate-500">{earnings?.total_reviews || 0} avis</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Route className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900" data-testid="total-distance">
                  {Math.round(history?.total_distance_km || 0)} km
                </p>
                <p className="text-xs text-slate-500">Distance totale</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Map + Delivery List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Map */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-['Barlow_Condensed'] text-lg font-bold text-slate-900 uppercase">
              Carte des trajets
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedDelivery ? (
              <Suspense fallback={
                <div className="h-[300px] bg-slate-100 rounded-lg flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              }>
                <div className="h-[300px]" data-testid="history-map-container">
                  <RouteMap
                    pickupCity={selectedDelivery.pickup_address?.city}
                    deliveryCity={selectedDelivery.delivery_address?.city}
                    pickupLat={selectedDelivery.pickup_address?.lat}
                    pickupLng={selectedDelivery.pickup_address?.lng}
                    deliveryLat={selectedDelivery.delivery_address?.lat}
                    deliveryLng={selectedDelivery.delivery_address?.lng}
                  />
                </div>
              </Suspense>
            ) : (
              <div className="h-[300px] bg-slate-100 rounded-lg flex items-center justify-center">
                <p className="text-sm text-slate-400">Aucune livraison</p>
              </div>
            )}
            {selectedDelivery && (
              <div className="mt-3 flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-green-500" />
                  <span className="text-slate-700">{selectedDelivery.pickup_address?.city}</span>
                  <span className="text-slate-400">→</span>
                  <MapPin className="w-4 h-4 text-red-500" />
                  <span className="text-slate-700">{selectedDelivery.delivery_address?.city}</span>
                </div>
                <span className="text-slate-500 font-medium">{selectedDelivery.distance_km} km</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Delivery List */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-['Barlow_Condensed'] text-lg font-bold text-slate-900 uppercase">
              Livraisons ({history?.deliveries?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[380px] overflow-y-auto" data-testid="delivery-list">
              {(!history?.deliveries || history.deliveries.length === 0) && (
                <div className="text-center py-8">
                  <Truck className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">Aucune livraison effectuee</p>
                </div>
              )}
              {history?.deliveries?.map((d) => (
                <button
                  key={d.id}
                  onClick={() => setSelectedDelivery(d)}
                  className={`w-full text-left p-3 rounded-lg border transition-all ${
                    selectedDelivery?.id === d.id
                      ? 'border-orange-300 bg-orange-50'
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                  data-testid={`delivery-item-${d.id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
                      <span>{d.pickup_address?.city}</span>
                      <span className="text-slate-400">→</span>
                      <span>{d.delivery_address?.city}</span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[d.status] || 'bg-slate-100 text-slate-600'}`}>
                      {statusLabels[d.status] || d.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(d.created_at).toLocaleDateString('fr-FR')}
                    </span>
                    <span className="flex items-center gap-1">
                      <Route className="w-3 h-3" />
                      {d.distance_km} km
                    </span>
                    <span className="flex items-center gap-1">
                      <Weight className="w-3 h-3" />
                      {d.weight} kg
                    </span>
                    {d.final_price && (
                      <span className="font-medium text-green-600">
                        {formatPrice(d.final_price)}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Earnings */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-['Barlow_Condensed'] text-lg font-bold text-slate-900 uppercase">
            Revenus ce mois
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-green-50 rounded-lg text-center">
              <p className="text-3xl font-bold text-green-700">{formatPrice(earnings?.month_earnings || 0)}</p>
              <p className="text-sm text-green-600 mt-1">Ce mois</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg text-center">
              <p className="text-3xl font-bold text-blue-700">{formatPrice(earnings?.pending_amount || 0)}</p>
              <p className="text-sm text-blue-600 mt-1">En attente ({earnings?.pending_count || 0})</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg text-center">
              <p className="text-3xl font-bold text-slate-700">{formatPrice(earnings?.completed_amount || 0)}</p>
              <p className="text-sm text-slate-600 mt-1">Versé ({earnings?.completed_count || 0})</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CarrierHistoryPage;
